'use client';

import { useState } from 'react';
import type { LinkProps } from 'next/link';
import useCartState from '../useCartState';
import { useI18n } from '~/features/i18n';
import ActionRow from '~/components/layout/ActionRow';
import { ButtonLink, Callout, Icon } from '~/components/primitives';
import EmptyCartAlert from '../EmptyCartAlert';
import { FaqDropdown } from '../FaqDropdown';
import { CartItemCard } from '../CartItemCard';
import { CartItemLocationLine } from '../CartItemLocationLine';
import { cn } from '~/utils';
import { usePortalCtx, usePortalHREF_relative, usePortalHREFs } from '~/features/global/portal';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { Checkout_ContinueButton } from '../checkout-continue-button';
import CheckoutInfoCard from './CheckoutInfoCard';
import { PORTAL_RELATIVE_HREF_CART } from '../hooks';
import Link from 'next/link';
import Image from 'next/image';

export const CheckoutPageTemplate: React.FC<
    React.PropsWithChildren<{
        previousButton: null | {
            href: LinkProps['href'];
            label?: React.ReactNode;
        };
        nextButton: {
            onClick: () => void;
            label?: React.ReactNode;
            disabled?: boolean;
        };

        sideBarContent?: React.ReactNode;
    }>
> = props => {
    const i18n = useI18n();
    const cartSDK = useCartState();

    const cartItems = useSuspenseQuery(cartSDK.cartItemsGrouped_queryOptions());

    return (
        <div>
            <div className="mb-20 lg:flex lg:justify-between lg:gap-x-20">
                <div className="max-w-3xl flex-1">
                    {!cartItems.data?.all.length && <EmptyCartAlert />}

                    {!!cartItems.data?.all.length && (
                        <MobileCartSummary cartItems={cartItems.data?.all ?? []} />
                    )}

                    {props.children}

                    <Callout
                        className="mt-10"
                        type="success"
                        title={i18n._('A quotation request is non-binding.')}
                        description={i18n._('checkout.quotation_request.description')}
                    />
                </div>
                <div className="hidden lg:block lg:max-w-[400px]">
                    <CheckoutActiveCart cartItems={cartItems.data?.all ?? []} />
                    {props.sideBarContent}
                </div>
            </div>

            <ActionRow className="sticky bottom-2 z-2 flex justify-between gap-3 bg-white">
                {props.previousButton && (
                    <ButtonLink
                        className="w-auto shrink-0"
                        variant="outline"
                        iconBefore="arrow-left"
                        size="md"
                        href={props.previousButton.href}
                    >
                        {props.previousButton.label ?? i18n._('Previous step')}
                    </ButtonLink>
                )}
                <Checkout_ContinueButton
                    className="ml-auto w-full lg:w-auto"
                    disabled={props.nextButton.disabled ?? false}
                    onSubmit={() => props.nextButton.onClick()}
                >
                    {props.nextButton.label ?? i18n._('Next step')}
                </Checkout_ContinueButton>
            </ActionRow>
        </div>
    );
};

const MobileCartSummary: React.FC<{
    cartItems: React.ComponentProps<typeof CartItemCard>['item'][];
}> = ({ cartItems }) => {
    const i18n = useI18n();
    const portal = usePortalCtx();
    const [open, setOpen] = useState(false);

    const CART_HREF = usePortalHREF_relative(PORTAL_RELATIVE_HREF_CART);

    return (
        <div
            className={cn(
                'mb-6 rounded-xl border p-4 lg:hidden',
                portal.portal === 'BIZZ'
                    ? 'border-gray-200 bg-white'
                    : 'border-brown-200 bg-brown-50',
            )}
        >
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                aria-expanded={open}
                aria-controls="mobile-cart-summary"
                className="flex w-full items-center justify-between text-left"
            >
                <span className="text-sm font-bold text-primary">
                    {i18n._('checkout.cart-summary.title', undefined, {
                        message: 'Your cart',
                    })}
                    <span
                        className={cn(
                            'ml-1.5 text-xs font-normal',
                            portal.portal === 'BIZZ' ? 'text-primary' : 'text-blue-600',
                        )}
                    >
                        ({cartItems.length})
                    </span>
                </span>
                <Icon
                    name="chevron-down"
                    size="xs"
                    className={cn(
                        portal.portal === 'BIZZ' ? 'text-primary' : 'text-blue-600',
                        'transition-transform',
                        open && 'rotate-180',
                    )}
                />
            </button>

            {open && (
                <div id="mobile-cart-summary" className="mt-4">
                    <ul className="space-y-3">
                        {cartItems.map(item => (
                            <CartItemCard
                                key={item.id}
                                item={item}
                                size="sm"
                                readonly
                                showLocation
                            />
                        ))}
                    </ul>
                    <ButtonLink
                        href={CART_HREF}
                        variant="secondary"
                        size="sm"
                        className="mt-4 w-full"
                    >
                        {i18n._('checkout.cart-summary.change-cart', undefined, {
                            message: 'Change cart',
                        })}
                    </ButtonLink>
                </div>
            )}
        </div>
    );
};

const CheckoutActiveCart: React.FC<{
    cartItems: NonNullable<TRPC_ROUTER.Outputs['cart']['get']>['items'];
}> = props => {
    const i18n = useI18n();
    const portal = usePortalCtx();

    const hrefs = usePortalHREFs();
    const CART_HREF = usePortalHREF_relative(PORTAL_RELATIVE_HREF_CART);

    return (
        <CheckoutInfoCard
            title={i18n._('checkout.quotes.title', undefined, {
                message: 'Your Quotes',
            })}
            description={i18n._('checkout.quotes.description', undefined, {
                message:
                    'You will receive a separate quote per activity with additional information within 24 hours.',
            })}
        >
            <ul
                className={
                    portal.portal === 'BIZZ'
                        ? 'mt-8 divide-y divide-gray-200'
                        : 'mt-8 divide-y divide-brown-300'
                }
            >
                {props.cartItems.map((cartItem, index) => {
                    const featuredImage = cartItem.activity?.media?.header?.path ?? null;
                    if (!cartItem.activity) return;
                    const slug = cartItem.activity.group?.slug;

                    const variantName = cartItem.activityVariant?.name;

                    return (
                        <li key={cartItem.id} className="py-4 first:pt-0 last:pb-0">
                            <Link
                                href={
                                    slug && typeof slug === 'string'
                                        ? hrefs.PDP({ slug, id: cartItem.activity.id })
                                        : CART_HREF
                                }
                                className="flex items-center gap-x-5"
                            >
                                <div className="relative min-h-[75px] min-w-[100px] overflow-hidden rounded-lg bg-gray-100">
                                    {featuredImage && (
                                        <Image
                                            src={featuredImage}
                                            alt={cartItem.activity?.name ?? 'header'}
                                            fill
                                        />
                                    )}
                                </div>
                                <div>
                                    <p
                                        className={
                                            portal.portal === 'BIZZ'
                                                ? 'text-xs font-medium text-primary'
                                                : 'text-xs font-medium text-blue-500'
                                        }
                                    >
                                        {i18n._(
                                            'checkout.quote-number',
                                            { number: index + 1 },
                                            {
                                                message: 'Quote {number}',
                                            },
                                        )}
                                    </p>
                                    <p className="text-base font-bold text-primary">
                                        {cartItem.activity.name}
                                    </p>
                                    {!!variantName && (
                                        <p
                                            className={
                                                portal.portal === 'BIZZ'
                                                    ? 'text-sm text-primary'
                                                    : 'text-sm text-blue-600'
                                            }
                                        >
                                            {variantName}
                                        </p>
                                    )}
                                    <CartItemLocationLine item={cartItem} />
                                </div>
                            </Link>
                        </li>
                    );
                })}
            </ul>
            <ButtonLink
                href={CART_HREF}
                variant="outline"
                iconBefore="arrow-left"
                size="sm"
                className="mt-6 w-full"
            >
                {i18n._('checkout.quote.back-to-cart', undefined, {
                    message: 'Back to cart',
                })}
            </ButtonLink>
            <FaqDropdown source="checkout_sidebar" className="mt-8" />
        </CheckoutInfoCard>
    );
};
